import consola from 'consola'
import { computed, ref } from 'vue'
import { apiAdminFunnels, apiAdminUtm } from '../api/apiAdminFunnels'
import type {
  FunnelQueryParams,
  FunnelReport,
  FunnelType,
  FunnelsCatalogItem,
  UtmReport,
} from '~/utils/types/funnels'

const logger = consola.withTag('admin-funnels')

/** По умолчанию две недели: за меньший срок воронка шумит на единичных сессиях. */
const DEFAULT_PERIOD_DAYS = 14

const toDateInput = (date: Date): string => date.toISOString().slice(0, 10)

/**
 * Состояние страницы воронок админки.
 *
 * Отчёт грузится по одной воронке за раз: считать все четыре сразу незачем — админ
 * смотрит их последовательно, а каждый запрос это агрегация по всему потоку событий.
 */
export function useAdminFunnels() {
  const catalog = ref<FunnelsCatalogItem[]>([])
  const timezone = ref('')
  const report = ref<FunnelReport | null>(null)
  const utmReport = ref<UtmReport | null>(null)

  const activeType = ref<FunnelType>('cart')
  const isLoading = ref(false)
  const errorMessage = ref<string | null>(null)

  const filters = ref<FunnelQueryParams>({
    startDate: toDateInput(new Date(Date.now() - DEFAULT_PERIOD_DAYS * 24 * 60 * 60 * 1000)),
    endDate: toDateInput(new Date()),
  })

  /**
   * Шаги для отрисовки полосами: ширина считается от первого шага, поэтому у самого
   * широкого всегда 100% — так видно форму воронки, а не абсолютные числа.
   */
  const stepsWithWidth = computed(() => {
    const steps = report.value?.steps ?? []
    const first = steps[0]?.sessions ?? 0

    return steps.map((step) => ({
      ...step,
      widthPercent: first > 0 ? Math.max(2, Math.round((step.sessions / first) * 100)) : 0,
    }))
  })

  /**
   * Шаг с наибольшей потерей — то, что чинить первым.
   * Необязательные шаги не рассматриваем: у них «падение» означает товар без добавок.
   */
  const worstStep = computed(() => {
    const steps = (report.value?.steps ?? []).filter((step) => !step.optional).slice(1)
    if (steps.length === 0) return null

    return steps.reduce((worst, step) => (step.dropoff > worst.dropoff ? step : worst))
  })

  const loadCatalog = async (): Promise<void> => {
    try {
      const data = await apiAdminFunnels.getCatalog()
      catalog.value = data.funnels
      timezone.value = data.timezone
    } catch (error) {
      logger.error('Не удалось загрузить справочник воронок', error)
    }
  }

  /**
   * Отдача рекламы. Грузится отдельно от воронки: это другой источник данных —
   * оплаченные заказы, а не поток событий, — и он не зависит от выбранной воронки.
   */
  const loadUtmReport = async (): Promise<void> => {
    try {
      utmReport.value = await apiAdminUtm.getReport({
        startDate: filters.value.startDate,
        endDate: filters.value.endDate,
      })
    } catch (error) {
      logger.error('Не удалось загрузить отчёт по рекламе', error)
      utmReport.value = null
    }
  }

  const loadReport = async (type: FunnelType = activeType.value): Promise<void> => {
    activeType.value = type
    isLoading.value = true
    errorMessage.value = null

    try {
      report.value = await apiAdminFunnels.getFunnel(type, filters.value)
    } catch (error) {
      logger.error('Не удалось загрузить воронку', error)
      errorMessage.value = 'Не удалось загрузить отчёт. Попробуйте обновить страницу.'
      report.value = null
    } finally {
      isLoading.value = false
    }
  }

  const setFilters = async (next: Partial<FunnelQueryParams>): Promise<void> => {
    filters.value = { ...filters.value, ...next }
    await Promise.all([loadReport(), loadUtmReport()])
  }

  const init = async (): Promise<void> => {
    await Promise.all([loadCatalog(), loadReport(), loadUtmReport()])
  }

  return {
    catalog,
    timezone,
    report,
    utmReport,
    activeType,
    isLoading,
    errorMessage,
    filters,
    stepsWithWidth,
    worstStep,
    init,
    loadReport,
    loadUtmReport,
    setFilters,
  }
}
